import { fromString } from 'ezmoney';
import { useQuery, type UseQueryResult } from 'react-query';

import { getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdBreakdowns } from 'src/client/spendesk-api';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';

import { getApiKey } from './keys';
import { type PeriodInterval } from '../../models/periodInterval';
import { transformDimensionTree } from '../helpers/budgetTree';
import { convertDimensionFromApi } from '../helpers/convertDimension';
import { convertDimensionValueFromApi } from '../helpers/convertDimensionValue';
import {
  type ApiBreakdownTree,
  type ApiBreakdown,
  type Breakdown,
  type BreakdownTree,
} from '../models/budgetBreakdown';
import { buildApiPeriod, buildPeriod } from '../models/period';

const convertBreakdown = (breakdown: ApiBreakdown): Breakdown => ({
  allocated: fromString(breakdown.allocated),
  remaining: fromString(breakdown.remaining),
  obligated: {
    committed: fromString(breakdown.obligated.committed),
    used: fromString(breakdown.obligated.used),
  },
  exceeded: {
    committed: fromString(breakdown.exceeded.committed),
    used: fromString(breakdown.exceeded.used),
  },
});

export type BreakdownsResponse = {
  breakdowns: BreakdownTree;
  viewPeriod: PeriodInterval;
  budgetPeriod: PeriodInterval;
};

export const useGetBudgetBreakdowns = (
  budgetDefinitionId: string,
  period: PeriodInterval,
): UseQueryResult<BreakdownsResponse> => {
  const company = useCompany();
  const viewPeriod = buildApiPeriod(period);
  return useQuery(
    getApiKey([budgetDefinitionId, 'breakdowns', viewPeriod]),
    async () => {
      const response =
        await getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdBreakdowns({
          path: {
            companyId: company.id,
            budgetDefinitionId,
          },
          query: {
            viewStart: viewPeriod.start,
            viewEnd: viewPeriod.end,
          },
        });

      if (response.statusCode !== 200) {
        throw new Error('Failed to get budget definitions');
      }
      return {
        breakdowns: transformDimensionTree(
          response.body.breakdowns as ApiBreakdownTree,
          {
            transformDimension: convertDimensionFromApi,
            transformValues: convertDimensionValueFromApi,
            transformOutcome: convertBreakdown,
          },
        ),
        viewPeriod: buildPeriod(response.body.viewPeriod),
        budgetPeriod: buildPeriod(response.body.budgetPeriod),
      };
    },
    {
      retry: false,
    },
  );
};
